import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, RefreshControl } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import ContentContainer from '../../src/components/ContentContainer';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import { useNetwork } from '../../src/context/NetworkContext';
import { useResponsive, rs, fs } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';

const MOBILE_BREAKPOINT = 768;
// ─── Types ───

interface Stats {
  users: number;
  owners: number;
  locations: number;
  bays: number;
  transactions: number;
  washSessions: number;
}

interface SystemHealth {
  mqtt: {
    connected: boolean;
    connectedSince: string | null;
    lastConnectedAt: string | null;
    lastDisconnectedAt: string | null;
    reconnectAttempts: number;
    lastHeartbeatAt: string | null;
    selfHealAttempts: number;
    lastError: string | null;
    uptimeSeconds: number | null;
    disconnects24h: number;
    messagesLastMinute: number;
    messagesPerMinuteAvg5m: number;
    stalePruneCount: number;
  };
  controllers: {
    total: number;
    online: number;
    offline: number;
    assignedTotal: number;
    assignedOnline: number;
    assignedOffline: number;
    unassignedOffline: number;
    unclaimed: number;
    firmwareVersions: Record<string, number>;
    otaPending: number;
    avgRssi: number | null;
    avgFreeHeap: number | null;
    devices: {
      id: string;
      isOnline: boolean;
      lastSeen: string | null;
      firmwareVersion: string | null;
      wifiRssi: number | null;
      uptimeSeconds: number | null;
      freeHeap: number | null;
      bayAssigned: boolean;
    }[];
  };
  database: {
    connected: boolean;
    sizeMb: number;
    controllerLogs: number;
    lastBackup: {
      filename: string | null;
      sizeMb: number | null;
      ageHours: number | null;
      modifiedAt: string | null;
    };
  };
  washes: {
    activeSessions: number;
    failedPayments24h: number;
    today: number;
    activeSubscriptions: number;
    activeBundles: number;
    stuckSessions: number;
  };
  server: {
    diskUsedPct: number;
    memUsedMb: number;
    memTotalMb: number;
    processUptimeSec: number;
    nodeVersion: string;
  };
  stripe: {
    webhookEventsLast24h: number;
    webhookEventsLastHour: number;
    webhookErrors24h: number;
    lastWebhookAt: string | null;
    subscriptionsPastDue: number;
    refunds24hCents: number;
    refunds24hCount: number;
    refunds7dCents: number;
    refunds7dCount: number;
    balanceAvailableCents: number | null;
    balancePendingCents: number | null;
    disputesOpen: number | null;
    payoutLastSuccessAt: string | null;
    payoutLastFailureAt: string | null;
    ownersWithRequirementsDue: number | null;
  };
  security: {
    failuresLast10Min: number;
    activeLockouts: number;
  };
  tls: {
    hosts: { host: string; daysUntilExpiry: number | null; expiresAt: string | null; error?: string }[];
  };
  jobs: {
    lastRuns: Record<string, {
      schedule: string;
      intervalMs: number;
      startedAt: string | null;
      finishedAt: string | null;
      status: 'ok' | 'error' | 'running' | 'never';
      durationMs: number | null;
      error: string | null;
      overdue: boolean;
    }>;
  };
  onboarding: {
    ownersStuckOnboarding: number;
    sample: { id: string; email: string; businessName: string }[];
  };
}

type Severity = 'good' | 'warning' | 'critical';

// ─── Helpers ───

const formatUptime = (sec: number): string => {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  return `${Math.floor(sec / 86400)}d ${Math.floor((sec % 86400) / 3600)}h`;
};

const timeAgo = (iso: string | null): string => {
  if (!iso) return 'Never';
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 5) return 'Just now';
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
};

const SEVERITY_COLORS: Record<Severity, string> = { good: '#059669', warning: '#d97706', critical: '#dc2626' };

function getSeverity(metric: string, value: number | boolean | null, health?: SystemHealth): Severity {
  switch (metric) {
    case 'mqtt': return value ? 'good' : 'critical';
    case 'db': return value ? 'good' : 'critical';
    case 'disk': return (value as number) >= 90 ? 'critical' : (value as number) >= 75 ? 'warning' : 'good';
    case 'memory': {
      if (!health?.server.memTotalMb) return 'good';
      const pct = ((value as number) / health.server.memTotalMb) * 100;
      return pct >= 85 ? 'critical' : pct >= 70 ? 'warning' : 'good';
    }
    case 'controllers': {
      if (!health) return 'good';
      // Admins can't act on a single owner's offline controller — only flag when
      // the pattern indicates a platform-wide MQTT issue (many at once).
      const offline = health.controllers.assignedOffline;
      const total = health.controllers.assignedTotal;
      if (offline < 3 || total === 0) return 'good';
      const pct = (offline / total) * 100;
      if (pct >= 80) return 'critical';
      if (pct >= 50) return 'warning';
      return 'good';
    }
    case 'backup': {
      if (!health) return 'good';
      const age = health.database.lastBackup.ageHours;
      if (age == null) return 'warning';
      return age > 48 ? 'critical' : age > 26 ? 'warning' : 'good';
    }
    case 'tls': {
      if (!health) return 'good';
      const min = Math.min(...health.tls.hosts.map(h => h.daysUntilExpiry ?? 999));
      return min <= 3 ? 'critical' : min <= 14 ? 'warning' : 'good';
    }
    case 'stripeWebhook': {
      if (!health) return 'good';
      if (health.stripe.webhookErrors24h > 5) return 'warning';
      if (health.stripe.webhookEventsLast24h === 0) return 'warning';
      return 'good';
    }
    case 'jobs': {
      if (!health) return 'good';
      const anyOverdue = Object.values(health.jobs.lastRuns).some(r => r.overdue);
      const anyError = Object.values(health.jobs.lastRuns).some(r => r.status === 'error');
      return anyError ? 'critical' : anyOverdue ? 'warning' : 'good';
    }
    case 'security': {
      if (!health) return 'good';
      const f = health.security.failuresLast10Min;
      const l = health.security.activeLockouts;
      return f > 50 || l > 5 ? 'warning' : 'good';
    }
    case 'rssi': {
      const rssi = value as number;
      return rssi > -60 ? 'good' : rssi > -75 ? 'warning' : 'critical';
    }
    case 'heap': {
      const heap = value as number;
      return heap > 50000 ? 'good' : heap > 20000 ? 'warning' : 'critical';
    }
    case 'failedPayments': return (value as number) > 0 ? 'warning' : 'good';
    case 'selfHeal': return (value as number) > 0 ? 'warning' : 'good';
    case 'reconnect': return (value as number) > 5 ? 'critical' : (value as number) > 0 ? 'warning' : 'good';
    case 'mqttFlap': return (value as number) >= 5 ? 'warning' : 'good';
    case 'staleLogs': return (value as number) > 0 ? 'warning' : 'good';
    case 'dbSize': return (value as number) > 500 ? 'warning' : 'good';
    case 'stuckSessions': return (value as number) > 0 ? 'warning' : 'good';
    default: return 'good';
  }
}

// ─── Components ───

function SeverityDot({ severity, size = 10 }: { severity: Severity; size?: number }) {
  return (
    <View style={{
      width: size, height: size, borderRadius: size / 2,
      backgroundColor: SEVERITY_COLORS[severity],
    }} />
  );
}

function SeverityBadge({ severity, label, styles }: { severity: Severity; label: string; styles: ReturnType<typeof createStyles> }) {
  return (
    <View style={[styles.badge, { backgroundColor: SEVERITY_COLORS[severity] + '18' }]}>
      <SeverityDot severity={severity} size={8} />
      <Text style={[styles.badgeText, { color: SEVERITY_COLORS[severity] }]}>{label}</Text>
    </View>
  );
}

function MetricRow({ label, value, severity, sub, styles }: {
  label: string; value: string | number; severity?: Severity; sub?: string;
  styles: ReturnType<typeof createStyles>;
}) {
  return (
    <View style={styles.metricRow}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
        {severity && <SeverityDot severity={severity} />}
        <Text style={styles.metricLabel}>{label}</Text>
      </View>
      <View style={{ alignItems: 'flex-end' }}>
        <Text style={[styles.metricValue, severity && { color: SEVERITY_COLORS[severity] }]}>{value}</Text>
        {sub && <Text style={styles.metricSub}>{sub}</Text>}
      </View>
    </View>
  );
}

function HealthCard({ title, icon, overallSeverity, children, styles }: {
  title: string; icon: string; overallSeverity: Severity;
  children: React.ReactNode; styles: ReturnType<typeof createStyles>;
}) {
  return (
    <View style={styles.healthCard}>
      <View style={styles.healthCardHeader}>
        <View style={[styles.healthIconCircle, { backgroundColor: SEVERITY_COLORS[overallSeverity] + '18' }]}>
          <Ionicons name={icon as never} size={20} color={SEVERITY_COLORS[overallSeverity]} />
        </View>
        <Text style={styles.healthCardTitle}>{title}</Text>
        <SeverityBadge severity={overallSeverity} label={overallSeverity === 'good' ? 'Healthy' : overallSeverity === 'warning' ? 'Warning' : 'Critical'} styles={styles} />
      </View>
      {children}
    </View>
  );
}

function BarMeter({ value, max, severity, styles }: {
  value: number; max: number; severity: Severity; styles: ReturnType<typeof createStyles>;
}) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <View style={styles.barOuter}>
      <View style={[styles.barInner, { width: `${pct}%`, backgroundColor: SEVERITY_COLORS[severity] }]} />
    </View>
  );
}

// ─── Main Dashboard ───

export default function AdminDashboard() {
  const { colors } = useTheme();
  const { width } = useResponsive();
  const isMobile = width < MOBILE_BREAKPOINT;
  const styles = useStyles(colors, isMobile);
  const toast = useToast();
  const { isOffline } = useNetwork();
  const [stats, setStats] = useState<Stats | null>(null);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = async (signal?: AbortSignal) => {
    try {
      const [statsData, healthData] = await Promise.all([
        api<Stats>('/api/admin/stats'),
        api<SystemHealth>('/api/admin/system-health'),
      ]);
      if (signal?.aborted) return;
      setStats(statsData);
      setHealth(healthData);
    } catch (err: any) {
      if (!(signal?.aborted) && !isOffline) {
        toast.show({ type: 'error', title: 'Failed to load dashboard', message: err?.message ?? 'Unknown error' });
      }
    } finally {
      if (!(signal?.aborted)) { setLoading(false); setRefreshing(false); }
    }
  };

  useEffect(() => { const ac = new AbortController(); fetchAll(ac.signal); return () => ac.abort(); }, []);

  // Auto-refresh health every 30s. Silent on failure: the initial load surfaces
  // outages, and toasting every 30s while the user is offline would be spammy.
  useEffect(() => {
    const interval = setInterval(() => {
      api<SystemHealth>('/api/admin/system-health').then(setHealth).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>;
  }

  const h = health;

  // Compute overall system severity
  const severities: Severity[] = h ? [
    getSeverity('mqtt', h.mqtt.connected),
    getSeverity('db', h.database.connected),
    getSeverity('disk', h.server.diskUsedPct),
    getSeverity('memory', h.server.memUsedMb, h),
    getSeverity('controllers', 0, h),
    getSeverity('failedPayments', h.washes.failedPayments24h),
    getSeverity('backup', 0, h),
    getSeverity('tls', 0, h),
    getSeverity('jobs', 0, h),
    getSeverity('stripeWebhook', 0, h),
    getSeverity('security', 0, h),
    h.washes.stuckSessions > 0 ? 'warning' as Severity : 'good' as Severity,
  ] : [];
  const overallSeverity: Severity = severities.includes('critical') ? 'critical' : severities.includes('warning') ? 'warning' : 'good';

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchAll(); }} tintColor={colors.primary} />}
    >
      <ContentContainer role="admin">
      {/* ─── System Health ─── */}
      {h && (
        <>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>System Health</Text>
            <SeverityBadge severity={overallSeverity} label={overallSeverity === 'good' ? 'All Systems OK' : overallSeverity === 'warning' ? 'Needs Attention' : 'Action Required'} styles={styles} />
          </View>

          <View style={styles.healthGrid}>
            {/* MQTT Broker */}
            <HealthCard title="MQTT Broker" icon="radio" overallSeverity={
              !h.mqtt.connected ? 'critical' : h.mqtt.disconnects24h >= 5 || h.mqtt.selfHealAttempts > 0 ? 'warning' : 'good'
            } styles={styles}>
              <MetricRow label="Status" value={h.mqtt.connected ? 'Connected' : 'Disconnected'} severity={getSeverity('mqtt', h.mqtt.connected)} styles={styles} />
              <MetricRow label="Uptime" value={h.mqtt.uptimeSeconds != null ? formatUptime(h.mqtt.uptimeSeconds) : '—'} styles={styles} />
              <MetricRow label="Last Heartbeat" value={timeAgo(h.mqtt.lastHeartbeatAt)} styles={styles} />
              <MetricRow label="Disconnects (24h)" value={h.mqtt.disconnects24h} severity={getSeverity('mqttFlap', h.mqtt.disconnects24h)} styles={styles} />
              <MetricRow label="Msgs/min (5m avg)" value={h.mqtt.messagesPerMinuteAvg5m} sub={`${h.mqtt.messagesLastMinute} this minute`} styles={styles} />
              <MetricRow label="Reconnects" value={h.mqtt.reconnectAttempts} severity={getSeverity('reconnect', h.mqtt.reconnectAttempts)} styles={styles} />
              <MetricRow label="Self-Heals" value={h.mqtt.selfHealAttempts} severity={getSeverity('selfHeal', h.mqtt.selfHealAttempts)} styles={styles} />
              {h.mqtt.lastError && <MetricRow label="Last Error" value={h.mqtt.lastError} severity="critical" styles={styles} />}
            </HealthCard>

            {/* Controllers — only bay-assigned offline counts as a warning */}
            <HealthCard title="Controllers" icon="hardware-chip" overallSeverity={getSeverity('controllers', 0, h)} styles={styles}>
              <MetricRow label="Online" value={h.controllers.online} styles={styles} />
              <MetricRow
                label="Offline (assigned)"
                value={h.controllers.assignedOffline}
                severity={getSeverity('controllers', 0, h)}
                sub={h.controllers.assignedOffline > 0 && getSeverity('controllers', 0, h) === 'good' ? 'individual outages — owner notified' : undefined}
                styles={styles}
              />
              {h.controllers.unassignedOffline > 0 && (
                <MetricRow label="Offline (unassigned)" value={h.controllers.unassignedOffline} sub="inventory — normal" styles={styles} />
              )}
              {h.controllers.unclaimed > 0 && <MetricRow label="Unclaimed" value={h.controllers.unclaimed} styles={styles} />}
              {h.controllers.avgFreeHeap != null && <MetricRow label="Avg Free Heap" value={`${Math.round(h.controllers.avgFreeHeap / 1024)} KB`} severity={getSeverity('heap', h.controllers.avgFreeHeap)} styles={styles} />}
              {Object.entries(h.controllers.firmwareVersions).map(([fw, n]) => (
                <MetricRow key={fw} label={`FW ${fw}`} value={`${n} device${n > 1 ? 's' : ''}`} styles={styles} />
              ))}
            </HealthCard>

            {/* Server */}
            <HealthCard title="Server" icon="server" overallSeverity={
              getSeverity('disk', h.server.diskUsedPct) === 'critical' || getSeverity('memory', h.server.memUsedMb, h) === 'critical' ? 'critical'
              : getSeverity('disk', h.server.diskUsedPct) === 'warning' || getSeverity('memory', h.server.memUsedMb, h) === 'warning' ? 'warning'
              : 'good'
            } styles={styles}>
              <MetricRow label="Disk" value={`${h.server.diskUsedPct}%`} severity={getSeverity('disk', h.server.diskUsedPct)} styles={styles} />
              <BarMeter value={h.server.diskUsedPct} max={100} severity={getSeverity('disk', h.server.diskUsedPct)} styles={styles} />
              <MetricRow label="Memory" value={`${h.server.memUsedMb} / ${h.server.memTotalMb} MB`}
                severity={getSeverity('memory', h.server.memUsedMb, h)}
                sub={h.server.memTotalMb ? `${Math.round((h.server.memUsedMb / h.server.memTotalMb) * 100)}%` : undefined}
                styles={styles} />
              {h.server.memTotalMb > 0 && (
                <BarMeter value={h.server.memUsedMb} max={h.server.memTotalMb} severity={getSeverity('memory', h.server.memUsedMb, h)} styles={styles} />
              )}
              <MetricRow label="Backend Uptime" value={formatUptime(h.server.processUptimeSec)} styles={styles} />
              <MetricRow label="Node.js" value={h.server.nodeVersion} styles={styles} />
            </HealthCard>

            {/* Database */}
            <HealthCard title="Database" icon="server" overallSeverity={
              !h.database.connected ? 'critical' : getSeverity('dbSize', h.database.sizeMb) === 'warning' ? 'warning' : 'good'
            } styles={styles}>
              <MetricRow label="Status" value={h.database.connected ? 'Connected' : 'Disconnected'} severity={getSeverity('db', h.database.connected)} styles={styles} />
              <MetricRow label="Size" value={`${h.database.sizeMb} MB`} severity={getSeverity('dbSize', h.database.sizeMb)} styles={styles} />
              <MetricRow label="MQTT Logs" value={Number(h.database.controllerLogs).toLocaleString()} sub="auto-pruned >7d" styles={styles} />
              {h.mqtt.stalePruneCount > 0 && (
                <MetricRow label="Logs >8d (pruner stuck)" value={h.mqtt.stalePruneCount} severity="warning" styles={styles} />
              )}
            </HealthCard>

            {/* Backup */}
            <HealthCard title="Database Backup" icon="archive" overallSeverity={getSeverity('backup', 0, h)} styles={styles}>
              {h.database.lastBackup.modifiedAt ? (
                <>
                  <MetricRow label="Most Recent" value={timeAgo(h.database.lastBackup.modifiedAt)} severity={getSeverity('backup', 0, h)} styles={styles} />
                  <MetricRow label="Size" value={`${h.database.lastBackup.sizeMb ?? '—'} MB`} styles={styles} />
                  <MetricRow label="File" value={h.database.lastBackup.filename ?? '—'} styles={styles} />
                </>
              ) : (
                <MetricRow label="No backups found" value="—" severity="warning" sub="DB_BACKUP_DIR empty or unreadable" styles={styles} />
              )}
            </HealthCard>

            {/* Today's Activity */}
            <HealthCard title="Today's Activity" icon="pulse" overallSeverity={
              h.washes.stuckSessions > 0 ? 'warning' : getSeverity('failedPayments', h.washes.failedPayments24h)
            } styles={styles}>
              <MetricRow label="Washes Today" value={h.washes.today} styles={styles} />
              <MetricRow label="Active Now" value={h.washes.activeSessions} styles={styles} />
              <MetricRow label="Subscriptions" value={h.washes.activeSubscriptions} styles={styles} />
              <MetricRow label="Active Bundles" value={h.washes.activeBundles} styles={styles} />
              <MetricRow label="Failed Payments (24h)" value={h.washes.failedPayments24h} severity={getSeverity('failedPayments', h.washes.failedPayments24h)} styles={styles} />
              {h.washes.stuckSessions > 0 && (
                <MetricRow label="Stuck Sessions" value={h.washes.stuckSessions} severity="warning" sub=">1h past max end" styles={styles} />
              )}
            </HealthCard>

            {/* Stripe Health */}
            <HealthCard title="Stripe" icon="card" overallSeverity={getSeverity('stripeWebhook', 0, h)} styles={styles}>
              <MetricRow label="Webhooks (24h)" value={h.stripe.webhookEventsLast24h} styles={styles} />
              <MetricRow label="Webhooks (1h)" value={h.stripe.webhookEventsLastHour} styles={styles} />
              <MetricRow label="Webhook Errors (24h)" value={h.stripe.webhookErrors24h} severity={h.stripe.webhookErrors24h > 5 ? 'warning' : 'good'} styles={styles} />
              <MetricRow label="Last Webhook" value={timeAgo(h.stripe.lastWebhookAt)} styles={styles} />
              <MetricRow label="Disputes Open" value={h.stripe.disputesOpen ?? '—'} severity={(h.stripe.disputesOpen ?? 0) > 0 ? 'critical' : 'good'} styles={styles} />
              <MetricRow label="Subs Past Due" value={h.stripe.subscriptionsPastDue} severity={h.stripe.subscriptionsPastDue > 0 ? 'warning' : 'good'} styles={styles} />
              <MetricRow label="Refunds (24h)" value={`$${(h.stripe.refunds24hCents / 100).toFixed(2)}`} sub={`${h.stripe.refunds24hCount} refunds`} styles={styles} />
              <MetricRow label="Balance Available" value={h.stripe.balanceAvailableCents != null ? `$${(h.stripe.balanceAvailableCents / 100).toFixed(2)}` : '—'} styles={styles} />
              {h.stripe.ownersWithRequirementsDue != null && h.stripe.ownersWithRequirementsDue > 0 && (
                <MetricRow label="Owners w/ Reqs Due" value={h.stripe.ownersWithRequirementsDue} severity="warning" styles={styles} />
              )}
              <MetricRow label="Last Payout" value={timeAgo(h.stripe.payoutLastSuccessAt)} sub={h.stripe.payoutLastFailureAt ? `last fail: ${timeAgo(h.stripe.payoutLastFailureAt)}` : undefined} styles={styles} />
            </HealthCard>

            {/* Background Jobs */}
            <HealthCard title="Background Jobs" icon="timer" overallSeverity={getSeverity('jobs', 0, h)} styles={styles}>
              {Object.entries(h.jobs.lastRuns).map(([key, r]) => {
                const sev: Severity = r.status === 'error' ? 'critical' : r.overdue ? 'warning' : 'good';
                const valueText = r.status === 'never' ? 'Never run' :
                  r.status === 'error' ? 'Error' :
                  r.startedAt ? `${timeAgo(r.startedAt)}` : '—';
                return (
                  <MetricRow
                    key={key}
                    label={key}
                    value={valueText}
                    severity={sev}
                    sub={r.error ? r.error.slice(0, 60) : r.schedule}
                    styles={styles}
                  />
                );
              })}
            </HealthCard>

            {/* TLS Certificates */}
            {h.tls.hosts.length > 0 && (
              <HealthCard title="TLS Certificates" icon="lock-closed" overallSeverity={getSeverity('tls', 0, h)} styles={styles}>
                {h.tls.hosts.map(host => {
                  const days = host.daysUntilExpiry;
                  const sev: Severity = days == null ? 'good' : days <= 3 ? 'critical' : days <= 14 ? 'warning' : 'good';
                  return (
                    <MetricRow
                      key={host.host}
                      label={host.host}
                      value={days == null ? '—' : `${days}d`}
                      severity={sev}
                      sub={host.error || (host.expiresAt ? new Date(host.expiresAt).toLocaleDateString() : undefined)}
                      styles={styles}
                    />
                  );
                })}
              </HealthCard>
            )}

            {/* Security */}
            <HealthCard title="Security" icon="shield-checkmark" overallSeverity={getSeverity('security', 0, h)} styles={styles}>
              <MetricRow label="Failed Logins (10min)" value={h.security.failuresLast10Min} severity={h.security.failuresLast10Min > 50 ? 'warning' : 'good'} styles={styles} />
              <MetricRow label="Active Lockouts" value={h.security.activeLockouts} severity={h.security.activeLockouts > 5 ? 'warning' : 'good'} styles={styles} />
              {h.onboarding.ownersStuckOnboarding > 0 && (
                <MetricRow label="Owners stuck onboarding" value={h.onboarding.ownersStuckOnboarding} sub="has controllers, no Stripe" styles={styles} />
              )}
            </HealthCard>

            {/* Platform Overview */}
            <HealthCard title="Platform" icon="grid" overallSeverity="good" styles={styles}>
              <MetricRow label="Customers" value={stats?.users ?? 0} styles={styles} />
              <MetricRow label="Owners" value={stats?.owners ?? 0} styles={styles} />
              <MetricRow label="Locations" value={stats?.locations ?? 0} styles={styles} />
              <MetricRow label="Bays" value={stats?.bays ?? 0} styles={styles} />
              <MetricRow label="Transactions" value={stats?.transactions ?? 0} styles={styles} />
            </HealthCard>
          </View>
        </>
      )}
      </ContentContainer>
    </ScrollView>
  );
}

// ─── Styles ───

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: isMobile ? spacing.md : spacing.lg, paddingBottom: 48 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  sectionTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },

  // Health cards
  healthGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md, marginBottom: spacing.md },
  healthCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.lg,
    width: isMobile ? '100%' : '48.5%', minWidth: 280,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1,
  },
  healthCardHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: spacing.md },
  healthIconCircle: { width: rs(36), height: rs(36), borderRadius: rs(18), justifyContent: 'center', alignItems: 'center' },
  healthCardTitle: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text, flex: 1 },

  // Metrics
  metricRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6 },
  metricLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  metricValue: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  metricSub: { fontSize: fs(11), color: c.textSecondary },

  // Badges
  badge: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  badgeText: { fontSize: fs(12), fontWeight: fontWeight.semibold },

  // Bar meter
  barOuter: { height: 6, backgroundColor: c.borderLight, borderRadius: 3, marginTop: 4, marginBottom: 8 },
  barInner: { height: 6, borderRadius: 3 },

});

const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
